import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Fab, Box, Typography, IconButton, Avatar, useMediaQuery, useTheme,
  Paper, TextField, Slide, Button
} from '@mui/material';
import {
  Chat as ChatIcon, Close as CloseIcon, SmartToy as BotIcon,
  SmartToy, Send as SendIcon, ArrowBack as BackIcon,
  School as TeachIcon, Check as CheckIcon
} from '@mui/icons-material';
import axios from 'axios';
import faqData from '../data/chatbotFAQ';

const STORAGE_KEY = 'chatbot_user_faqs';
const API_FAQ = '/api/faq';

const roleTitles = {
  ADMIN: 'Administrateur', DIRECTEUR: 'Directeur', SCOLARITE: 'Scolarité', ENSEIGNANT: 'Enseignant',
  ETUDIANT: 'Étudiant', CONSULTANT: 'Consultant'
};
const roleGreetings = {
  ADMIN: 'Bonjour Administrateur ! Je suis votre assistant. Posez votre question ou choisissez parmi les suggestions :',
  DIRECTEUR: 'Bonjour Directeur ! Je suis votre assistant. Posez votre question ou choisissez parmi les suggestions :',
  SCOLARITE: 'Bonjour ! Je suis votre assistant scolarité. Posez votre question ou choisissez parmi les suggestions :',
  ENSEIGNANT: 'Bonjour Enseignant ! Je suis là pour vous guider. Tapez votre question ou explorez les suggestions :',
  ETUDIANT: 'Salut ! Besoin d\'aide sur vos notes, paiements ou emploi du temps ? Posez-moi une question !',
  CONSULTANT: 'Bonjour Consultant ! Je vous aide à naviguer dans l\'application. Que voulez-vous savoir ?'
};
const botGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const stopWords = new Set([
  'le', 'la', 'les', 'de', 'des', 'du', 'un', 'une', 'et', 'ou', 'est', 'pas',
  'que', 'qui', 'quoi', 'dans', 'pour', 'sur', 'avec', 'sans', 'par', 'je',
  'tu', 'il', 'elle', 'on', 'nous', 'vous', 'ils', 'elles', 'ce', 'cet',
  'cette', 'ces', 'mon', 'ton', 'son', 'ma', 'ta', 'sa', 'mes', 'tes', 'ses',
  'au', 'aux', 'en', 'y', 'à', 'ai', 'a', 'ont', 'sont', 'fait', 'faire',
  'plus', 'moins', 'très', 'peut', 'peux', 'veu', 'veux', 'vais', 'va',
  'comment', 'pourquoi', 'quand', 'où', 'combien', 'quel', 'quelle',
  'quels', 'quelles', 'être', 'avoir', 'aller', 'voir', 'dire', 'pouvoir'
]);

const tokenize = (text) => {
  return text.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 2 && !stopWords.has(w));
};

const scoreQuestion = (userTokens, faqQuestion) => {
  const faqTokens = tokenize(faqQuestion);
  if (faqTokens.length === 0) return 0;
  const intersection = userTokens.filter(t => faqTokens.includes(t));
  const union = [...new Set([...userTokens, ...faqTokens])];
  const jaccard = union.length > 0 ? intersection.length / union.length : 0;
  const overlap = userTokens.length > 0 ? intersection.length / userTokens.length : 0;
  const faqOverlap = faqTokens.length > 0 ? intersection.length / faqTokens.length : 0;
  return jaccard * 0.4 + overlap * 0.3 + faqOverlap * 0.3;
};

const findBestAnswer = (userText, allQA) => {
  const userTokens = tokenize(userText);
  if (userTokens.length === 0) return null;

  const scored = allQA.map(item => ({
    item,
    score: scoreQuestion(userTokens, item.q)
  })).filter(s => s.score > 0.05);

  if (scored.length === 0) return null;

  scored.sort((a, b) => b.score - a.score);
  const best = scored[0];

  return {
    question: best.item.q,
    answer: best.item.a,
    score: best.score,
    alternatives: scored.slice(1, 3).filter(s => s.score > 0.1).map(s => s.item.q)
  };
};

const fallbackAnswers = [
  "Je ne connais pas encore la réponse. Vous pouvez m'apprendre en cliquant sur \"Enseigner\" ci-dessous.",
  "Je n'ai pas d'information sur ce sujet. Cliquez sur \"Enseigner\" pour m'ajouter la réponse.",
  "Je ne peux pas répondre pour le moment. Si vous avez la réponse, utilisez le bouton \"Enseigner\".",
  "Désolé, je ne sais pas encore répondre à cette question. Aidez-moi à apprendre en cliquant sur \"Enseigner\"."
];

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userFAQs, setUserFAQs] = useState([]);
  const [teachFor, setTeachFor] = useState(null);
  const [teachAnswer, setTeachAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const teachInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  }, []);

  const role = user?.role || 'CONSULTANT';
  const roleKey = role === 'ETUDIANT' ? 'ETUDIANT' : role === 'ENSEIGNANT' ? 'ENSEIGNANT' : role === 'ADMIN' ? 'ADMIN' : role === 'DIRECTEUR' ? 'DIRECTEUR' : role === 'SCOLARITE' ? 'SCOLARITE' : 'CONSULTANT';

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setUserFAQs(JSON.parse(stored)); } catch { /* ignore */ }
    }
    axios.get(`${API_FAQ}?role=${roleKey}`).then(res => {
      if (Array.isArray(res.data) && res.data.length) {
        const api = res.data.map(f => ({ q: f.q, a: f.a, source: 'api', _id: f._id }));
        setUserFAQs(prev => {
          const merged = [...prev, ...api.filter(apiF => !prev.some(p => p.q.toLowerCase() === apiF.q.toLowerCase()))];
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          return merged;
        });
      }
    }).catch(() => {});
  }, [roleKey]);

  const allQA = useMemo(() => {
    const roleQA = faqData[roleKey] || [];
    const generalQA = faqData.GENERAL || [];
    return [...roleQA, ...generalQA, ...userFAQs];
  }, [roleKey, userFAQs]);

  const addMessage = useCallback((msg) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      addMessage({ from: 'bot', text: roleGreetings[roleKey] || roleGreetings.CONSULTANT, isGreeting: true });
    }
  }, [open, roleKey, addMessage, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  useEffect(() => {
    if (teachFor && teachInputRef.current) {
      setTimeout(() => teachInputRef.current?.focus(), 100);
    }
  }, [teachFor]);

  const handleOpen = () => { setOpen(true); setMessages([]); setInput(''); setShowSuggestions(false); setTeachFor(null); setTeachAnswer(''); };
  const handleClose = () => { setOpen(false); setShowSuggestions(false); setTeachFor(null); setTeachAnswer(''); };

  const askQuestion = (item) => {
    addMessage({ from: 'user', text: item.q });
    setShowSuggestions(false);
    setTeachFor(null);
    setTeachAnswer('');
    setInput('');
    setTimeout(() => {
      addMessage({ from: 'bot', text: item.a });
    }, 500);
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    addMessage({ from: 'user', text });
    setInput('');
    setShowSuggestions(false);
    setTeachFor(null);
    setTeachAnswer('');

    setTimeout(() => {
      const result = findBestAnswer(text, allQA);
      if (result && result.score > 0.15) {
        addMessage({ from: 'bot', text: result.answer, relatedQuestion: result.question });
        if (result.score < 0.4 && result.alternatives?.length) {
          setTimeout(() => {
            addMessage({ from: 'bot', text: 'Peut-être cherchiez-vous l\'une de ces questions :' });
            addMessage({ suggestions: result.alternatives });
          }, 300);
        }
      } else {
        const fallback = fallbackAnswers[Math.floor(Math.random() * fallbackAnswers.length)];
        addMessage({ from: 'bot', text: fallback, isFallback: true });
        setTeachFor(text);
        setTeachAnswer('');
      }
    }, 600);
  };

  const handleTeachSave = async () => {
    if (!teachAnswer.trim() || !teachFor) return;
    const newFAQs = [
      { q: teachFor, a: teachAnswer.trim(), source: 'user' },
      ...userFAQs,
    ];
    setUserFAQs(newFAQs);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newFAQs));
    setSaving(true);
    try {
      await axios.post(API_FAQ, { q: teachFor, a: teachAnswer.trim(), role: roleKey });
      addMessage({ from: 'bot', text: 'Merci ! J\'ai bien enregistré cette information. Je saurai répondre la prochaine fois.' });
    } catch {
      addMessage({ from: 'bot', text: 'Merci ! La réponse a été ajoutée localement.' });
    }
    setSaving(false);
    setTeachFor(null);
    setTeachAnswer('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTeachKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTeachSave();
    }
  };

  return (
    <>
      <Fab
        aria-label="chatbot"
        onClick={handleOpen}
        sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1200,
          background: botGradient, width: 56, height: 56,
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.45)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'scale(1.08)',
            boxShadow: '0 6px 28px rgba(102, 126, 234, 0.6)'
          }
        }}
      >
        <ChatIcon sx={{ color: '#fff' }} />
      </Fab>

      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            zIndex: 1199,
            width: isMobile ? 'calc(100vw - 32px)' : 380,
            height: isMobile ? 'calc(100vh - 120px)' : 520,
            maxHeight: 'calc(100vh - 120px)',
            display: 'flex', flexDirection: 'column',
            borderRadius: 2.5,
            boxShadow: '0 8px 40px rgba(0,0,0,0.15), 0 2px 10px rgba(0,0,0,0.08)',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.1)',
            '@keyframes widgetIn': {
              from: { opacity: 0, transform: 'translateY(16px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          <Box sx={{
            background: botGradient, color: '#fff', display: 'flex',
            alignItems: 'center', gap: 1.5, py: 1.5, px: 2,
            flexShrink: 0, minHeight: 60,
            borderTopLeftRadius: 2.5, borderTopRightRadius: 2.5
          }}>
            <Avatar sx={{
              width: 34, height: 34,
              background: 'rgba(255,255,255,0.2)',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              <BotIcon sx={{ color: '#fff', fontSize: 18 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.9rem' }}>
                Assistant SUP2I
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem' }}>
                {roleTitles[roleKey]} · En ligne
              </Typography>
            </Box>
            <IconButton onClick={handleClose} sx={{ color: '#fff', p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{
            flex: 1, overflow: 'auto', bgcolor: '#f0f2f5', p: 2,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#c4c4c4', borderRadius: 2 }
          }}>
            {messages.map((msg, idx) => (
              <React.Fragment key={idx}>
                {msg.suggestions ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 3, my: 1, animation: 'fadeIn 0.3s ease' }}>
                    {msg.suggestions.map((s, i) => (
                      <Paper key={i} elevation={0} onClick={() => {
                        const found = allQA.find(q => q.q === s);
                        if (found) askQuestion(found);
                      }} sx={{
                        px: 1.2, py: 0.6, borderRadius: 2, cursor: 'pointer',
                        bgcolor: '#fff', border: '1px solid #e5e7eb', fontSize: '0.75rem',
                        color: '#6d28d9', fontWeight: 500,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: '#f5f3ff', borderColor: '#c4b5fd' }
                      }}>
                        {s}
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: msg.from === 'bot' ? 'flex-start' : 'flex-end',
                    mb: 1.5,
                    animation: 'fadeIn 0.3s ease',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(8px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    {msg.from === 'bot' && (
                      <Avatar sx={{ width: 28, height: 28, mr: 1, mt: 0.5, flexShrink: 0, background: botGradient }}>
                        <SmartToy sx={{ color: '#fff', fontSize: 15 }} />
                      </Avatar>
                    )}
                    <Box sx={{ maxWidth: '78%' }}>
                      <Paper elevation={0} sx={{
                        p: 1.5, borderRadius: 2,
                        bgcolor: msg.from === 'bot' ? '#fff' : botGradient,
                        color: msg.from === 'bot' ? '#1f2937' : '#fff',
                        border: msg.from === 'bot' ? '1px solid #e5e7eb' : 'none',
                        borderBottomLeftRadius: msg.from === 'bot' ? 4 : 2,
                        borderBottomRightRadius: msg.from === 'bot' ? 2 : 4,
                        boxShadow: msg.from === 'bot'
                          ? '0 1px 3px rgba(0,0,0,0.06)'
                          : '0 2px 8px rgba(102, 126, 234, 0.25)'
                      }}>
                        <Typography variant="body2" sx={{
                          lineHeight: 1.6, fontSize: '0.82rem',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                        }}>
                          {msg.text}
                        </Typography>
                      </Paper>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.3, px: 0.5 }}>
                        <Typography variant="caption" sx={{
                          color: '#9ca3af', fontSize: '0.6rem',
                          flex: 1, textAlign: msg.from === 'bot' ? 'left' : 'right'
                        }}>
                          {msg.from === 'bot' ? 'Assistant SUP2I' : 'Vous'}
                        </Typography>
                        {msg.relatedQuestion && msg.from === 'bot' && (
                          <Typography variant="caption" sx={{
                            color: '#a5b4fc', fontSize: '0.55rem', fontStyle: 'italic'
                          }}>
                            {msg.relatedQuestion}
                          </Typography>
                        )}
                        {msg.source === 'user' && msg.from === 'bot' && (
                          <Typography variant="caption" sx={{
                            color: '#f59e0b', fontSize: '0.55rem', fontStyle: 'italic'
                          }}>
                            Appris
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </React.Fragment>
            ))}

            {teachFor && (
              <Box sx={{
                display: 'flex', justifyContent: 'flex-start', mb: 1.5,
                animation: 'fadeIn 0.3s ease'
              }}>
                <Box sx={{ maxWidth: '90%', width: '100%' }}>
                  <Paper elevation={0} sx={{
                    p: 1.5, borderRadius: 2, bgcolor: '#fefce8',
                    border: '1px solid #fde68a'
                  }}>
                    <Typography variant="caption" sx={{
                      color: '#92400e', fontWeight: 600, fontSize: '0.7rem',
                      display: 'flex', alignItems: 'center', gap: 0.5, mb: 1
                    }}>
                      <TeachIcon sx={{ fontSize: 14 }} />
                      Enseignez-moi la réponse
                    </Typography>
                    <Typography variant="body2" sx={{
                      fontSize: '0.78rem', color: '#78350f', mb: 1.5, fontWeight: 500
                    }}>
                      Q : {teachFor}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Tapez la réponse à cette question..."
                      value={teachAnswer}
                      onChange={(e) => setTeachAnswer(e.target.value)}
                      onKeyDown={handleTeachKeyDown}
                      inputRef={teachInputRef}
                      variant="outlined"
                      size="small"
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.8rem',
                          '& fieldset': { borderColor: '#fde68a' },
                          '&:hover fieldset': { borderColor: '#f59e0b' },
                          '&.Mui-focused fieldset': { borderColor: '#d97706' }
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => { setTeachFor(null); setTeachAnswer(''); }}
                        sx={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        Annuler
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!teachAnswer.trim() || saving}
                        onClick={handleTeachSave}
                        sx={{
                          bgcolor: '#d97706', fontSize: '0.75rem', textTransform: 'none',
                          '&:hover': { bgcolor: '#b45309' },
                          '&.Mui-disabled': { bgcolor: '#fcd34d', color: '#fff' }
                        }}
                      >
                        {saving ? 'Enregistrement...' : (
                          <><CheckIcon sx={{ fontSize: 14, mr: 0.3 }} /> Enseigner</>
                        )}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {showSuggestions && (
            <Box sx={{
              borderTop: '1px solid #e5e7eb', bgcolor: '#f9fafb',
              px: 2, py: 1.5, maxHeight: 180, overflow: 'auto', flexShrink: 0,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#c4c4c4', borderRadius: 2 }
            }}>
              <Typography variant="caption" sx={{
                color: '#6b7280', fontWeight: 600, mb: 1, display: 'block',
                fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5
              }}>
                Questions fréquentes ({allQA.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {allQA.map((item, idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    onClick={() => askQuestion(item)}
                    sx={{
                      p: 1, borderRadius: 1.5, cursor: 'pointer',
                      border: '1px solid #f3f4f6',
                      transition: 'all 0.15s',
                      '&:hover': { bgcolor: '#f5f3ff', borderColor: '#c4b5fd', transform: 'translateX(4px)' }
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, fontSize: '0.78rem' }}>
                      {item.q}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{
            p: 1.5, bgcolor: '#fff', borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0
          }}>
            <IconButton
              onClick={() => setShowSuggestions(v => !v)}
              size="small"
              sx={{
                color: showSuggestions ? '#667eea' : '#9ca3af',
                bgcolor: showSuggestions ? '#f5f3ff' : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: '#f5f3ff', color: '#667eea' }
              }}
            >
              <BackIcon sx={{ fontSize: 18, transform: showSuggestions ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
            </IconButton>
            <TextField
              fullWidth
              placeholder="Tapez votre question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5, bgcolor: '#f9fafb', fontSize: '0.82rem',
                  '& fieldset': { borderColor: '#e5e7eb' },
                  '&:hover fieldset': { borderColor: '#c4b5fd' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                }
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim()}
              sx={{
                bgcolor: input.trim() ? botGradient : '#e5e7eb',
                color: '#fff', borderRadius: 2, width: 36, height: 36,
                transition: 'all 0.2s',
                '&:hover': { transform: input.trim() ? 'scale(1.05)' : 'none' },
                '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

export default ChatBot;

